import { Popover } from '@mantine/core';
import { memo, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import type { OverviewPayload } from '../../api/overview';
import { useSession } from '../../api/session';
import { useDeepLinks } from '../../lib/deepLinks';
import { dayToneAt, m2t, phaseLabel, plM } from '../../lib/ui';
import type { WallClock } from '../../live/clock';
import { useUi } from '../../state/ui';
import {
  barSectors,
  buildHeat,
  cellRows,
  defaultDays,
  locateHour,
  locateSpan,
  rowsAt,
  type HeatDay,
  type HeatLenses,
  type HeatModel,
  type HeatRow,
} from './derive';
import { HeatOverlay, trackX } from './HeatOverlay';
import './heat.css';

/**
 * Pokritost po urah (SPEC §1.1A): sectors × hours, the event days stacked on
 * one clock, each cell counting the posts nobody is coming to (red) or whose
 * person has not confirmed (amber). The model is `derive.ts`; this draws it,
 * and the popover turns a cell into the same actions as "Kaj me blokira".
 */

/**
 * What a popover is open on. It names an hour, never a column index: the axis is
 * rebuilt from every window whenever /overview refetches.
 */
type Sel =
  | { kind: 'cell'; key: string; dayId: string; sectorKey: string; hour: number }
  | { kind: 'bar'; key: string; dayId: string; hour: number }
  | { kind: 'span'; key: string; dayId: string; sectorKey: string; postId: string; fromMin: number };

type Box = { left: number; top: number; width: number; height: number };

const PHASE_TONE = {
  past: { bg: 'oklch(0.965 0.005 95)', fg: 'oklch(0.55 0.012 90)' },
  live: { bg: 'oklch(0.95 0.04 25)', fg: 'oklch(0.48 0.15 25)' },
  next: { bg: 'oklch(0.96 0.02 245)', fg: 'oklch(0.42 0.07 245)' },
} as const;

const shortDate = (iso: string) => {
  const [, m, d] = iso.split('-');
  return `${Number(d)}. ${Number(m)}.`;
};

function Chevron() {
  return (
    <svg className="rr-heat-chev" viewBox="0 0 16 16" aria-hidden="true">
      <path
        d="M4 6l4 4 4-4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export const CoverageHeat = memo(function CoverageHeat({
  data,
  clock,
}: {
  data: OverviewPayload;
  clock: WallClock;
}) {
  const heat = useUi((s) => s.heat);
  const setHeat = useUi((s) => s.setHeat);
  const { can } = useSession();
  const canComms = can('comms:read');
  const { goGap, goShift, goPerson, goTimelineAt } = useDeepLinks();

  const model = useMemo(
    () =>
      buildHeat(
        data,
        {
          short: heat.short,
          pending: heat.pending,
          urgentOnly: heat.urgentOnly,
          hideQuiet: heat.hideQuiet,
        },
        clock,
      ),
    [data, heat.short, heat.pending, heat.urgentOnly, heat.hideQuiet, clock],
  );
  const lenses: HeatLenses = { short: heat.short, pending: heat.pending };

  // Which days start open is decided once, so a day ending while somebody reads
  // it does not fold shut under them.
  useEffect(() => {
    if (heat.days === null) setHeat({ days: defaultDays(data, clock) });
  }, [heat.days, data, clock, setHeat]);
  const isOpen = (d: HeatDay) => heat.days?.[d.dayId] ?? d.phase !== 'past';

  const gridRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [sel, setSel] = useState<{ sel: Sel; box: Box } | null>(null);
  const [focusKey, setFocusKey] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ i: number; n: number } | null>(null);
  // Click-outside closes on mousedown, before the click lands: a press on the
  // cell that is already open must not open it again.
  const pressedOpen = useRef(false);

  const n = model.hours.length;
  const close = () => setSel(null);

  // A refetch can take away what a popover is open on (its hour off the axis, a
  // post gone) or move it (a column added before it): close rather than show
  // another hour's rows or float beside the wrong cell.
  useEffect(() => {
    if (!sel) return;
    const s = sel.sel;
    const alive =
      s.kind === 'span'
        ? locateSpan(model, s.dayId, s.sectorKey, s.postId, s.fromMin)
        : locateHour(model, s.dayId, s.hour, s.kind === 'cell' ? s.sectorKey : undefined);
    const el = gridRef.current?.querySelector<HTMLElement>(`[data-heat-key="${CSS.escape(s.key)}"]`);
    const grid = gridRef.current?.getBoundingClientRect();
    const r = el?.getBoundingClientRect();
    const moved =
      !r || !grid || Math.abs(r.left - grid.left - sel.box.left) > 1 || Math.abs(r.top - grid.top - sel.box.top) > 1;
    if (!alive || moved) setSel(null);
  }, [model, sel]);

  const open = (el: HTMLElement, next: Sel) => {
    const grid = gridRef.current;
    if (!grid) return;
    const g = grid.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    setFocusKey(next.key);
    setSel({ sel: next, box: { left: r.left - g.left, top: r.top - g.top, width: r.width, height: r.height } });
  };

  const pressHandlers = (key: string) => ({
    onPointerDown: () => {
      pressedOpen.current = sel?.sel.key === key;
    },
    onFocus: () => setFocusKey(key),
  });
  const clickOpen = (el: HTMLElement, next: Sel) => {
    const wasOpen = pressedOpen.current;
    pressedOpen.current = false;
    if (wasOpen) return;
    open(el, next);
  };

  const toggleDay = (d: HeatDay) =>
    setHeat({ days: { ...(heat.days ?? defaultDays(data, clock)), [d.dayId]: !isOpen(d) } });
  const toggleSector = (key: string) =>
    setHeat({ sectors: { ...heat.sectors, [key]: !heat.sectors[key] } });

  const jumpTo = (dayId: string, hour: number) => {
    const i = model.hours.indexOf(hour);
    const day = model.days.find((d) => d.dayId === dayId);
    if (i < 0 || !day) return;
    if (!isOpen(day)) toggleDay(day);
    const scroller = scrollerRef.current;
    const grid = gridRef.current;
    if (scroller && grid) {
      const label = grid.querySelector<HTMLElement>('.rr-heat-axis .rr-heat-lab')?.offsetWidth ?? 0;
      const sum = grid.querySelector<HTMLElement>('.rr-heat-axis .rr-heat-sum')?.offsetWidth ?? 0;
      const x = label + ((grid.offsetWidth - label - sum) * (i + 0.5)) / n;
      scroller.scrollTo({ left: Math.max(0, x - scroller.clientWidth / 2), behavior: 'smooth' });
      grid.querySelector(`[data-day-band="${dayId}"]`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
    setFlash({ i, n: Date.now() });
  };

  // ---- keyboard: one tab stop, arrows walk the interactive cells ----
  const onGridKey = (ev: KeyboardEvent<HTMLDivElement>) => {
    if (ev.key === 'Escape' && sel) {
      close();
      return;
    }
    const dirs: Record<string, [number, number]> = {
      ArrowLeft: [0, -1],
      ArrowRight: [0, 1],
      ArrowUp: [-1, 0],
      ArrowDown: [1, 0],
    };
    const dir = dirs[ev.key];
    const from = (ev.target as HTMLElement).closest<HTMLElement>('[data-heat-key]');
    if (!dir || !from || !gridRef.current) return;
    ev.preventDefault();
    const all = [...gridRef.current.querySelectorAll<HTMLElement>('[data-heat-key]')];
    const row = Number(from.dataset.navRow);
    const col = Number(from.dataset.navCol);
    let target: HTMLElement | undefined;
    if (dir[0] === 0) {
      const same = all.filter((e) => Number(e.dataset.navRow) === row);
      target = same[same.indexOf(from) + dir[1]];
    } else {
      const rows = [...new Set(all.map((e) => Number(e.dataset.navRow)))].sort((a, b) => a - b);
      for (let k = rows.indexOf(row) + dir[0]; k >= 0 && k < rows.length; k += dir[0]) {
        const candidates = all.filter((e) => Number(e.dataset.navRow) === rows[k]);
        if (!candidates.length) continue;
        target = candidates.reduce((best, e) =>
          Math.abs(Number(e.dataset.navCol) - col) < Math.abs(Number(best.dataset.navCol) - col) ? e : best,
        );
        break;
      }
    }
    target?.focus();
  };

  // Every interactive element the keyboard can reach, in reading order, so one
  // of them holds the single tab stop; rows are numbered while rendering.
  const navKeys: string[] = [];
  let navRow = 0;
  const tabKey = () => (focusKey && navKeys.includes(focusKey) ? focusKey : navKeys[0]);
  for (const d of model.days) {
    d.hours.forEach((c) => c.open && navKeys.push(`b:${d.dayId}:${c.hour}`));
    if (!isOpen(d)) continue;
    for (const s of d.sectors) {
      s.cells.forEach((c) => (c.short || c.pending) && navKeys.push(`c:${s.key}:${c.hour}`));
      if (!heat.sectors[s.key]) continue;
      for (const p of s.posts)
        for (const sp of p.coverage)
          if (spanShown(sp.state, lenses)) navKeys.push(`s:${d.dayId}:${p.postId}:${sp.fromMin}`);
    }
  }
  const stop = tabKey();
  const tabIndexOf = (key: string) => (key === stop ? 0 : -1);

  const selKey = sel?.sel.key;

  return (
    <section className="rr-heat" aria-label="Pokritost po urah">
      <div className="rr-heat-head">
        <span
          style={{
            fontSize: '0.75rem',
            letterSpacing: '0.05em',
            fontWeight: 600,
            color: 'oklch(0.48 0.012 90)',
            whiteSpace: 'nowrap',
          }}
        >
          POKRITOST PO URAH
        </span>
        {model.hotspots.short ? (
          <button
            type="button"
            className="rr-heat-hot"
            onClick={() => jumpTo(model.hotspots.short!.dayId, model.hotspots.short!.hour)}
          >
            <span className="rr-heat-sw" style={{ background: 'var(--heat-r3)' }} />
            Največ manjka:
            <b>
              {model.hotspots.short.label.toLowerCase()} {model.hotspots.short.hour}:00
            </b>
            · {plM(model.hotspots.short.count)}
          </button>
        ) : null}
        {model.hotspots.pending ? (
          <button
            type="button"
            className="rr-heat-hot"
            onClick={() => jumpTo(model.hotspots.pending!.dayId, model.hotspots.pending!.hour)}
          >
            <span className="rr-heat-sw" style={{ background: 'var(--heat-a4)' }} />
            Največ nepotrjenih:
            <b>
              {model.hotspots.pending.label.toLowerCase()} {model.hotspots.pending.hour}:00
            </b>
            · {plM(model.hotspots.pending.count)}
          </button>
        ) : null}
      </div>

      <div className="rr-heat-controls">
        <button
          type="button"
          className="rr-heat-chip"
          aria-pressed={heat.short}
          onClick={() => setHeat({ short: !heat.short })}
        >
          <span className="rr-heat-sw" style={{ background: 'var(--heat-r3)' }} />
          Manjka oseba
          <span className="rr-heat-ramp" aria-hidden="true">
            <i style={{ background: 'var(--heat-r1)' }} />
            <i style={{ background: 'var(--heat-r2)' }} />
            <i style={{ background: 'var(--heat-r3)' }} />
            <i style={{ background: 'var(--heat-r4)' }} />
            <em>1 · 2–3 · 4–7 · 8+</em>
          </span>
        </button>
        <button
          type="button"
          className="rr-heat-chip"
          aria-pressed={heat.pending}
          onClick={() => setHeat({ pending: !heat.pending })}
        >
          <span className="rr-heat-sw" style={{ background: 'var(--heat-a4)' }} />
          Čaka potrditev
          <span className="rr-heat-ramp" aria-hidden="true">
            <i style={{ background: 'var(--heat-a1)' }} />
            <i style={{ background: 'var(--heat-a2)' }} />
            <i style={{ background: 'var(--heat-a3)' }} />
            <i style={{ background: 'var(--heat-a4)' }} />
          </span>
        </button>
        <span className="rr-heat-chip rr-heat-chip--static">
          <span className="rr-heat-sw" style={{ background: 'var(--heat-ok)' }} />
          Brez težav
        </span>
        <span className="rr-heat-divider" aria-hidden="true" />
        <button
          type="button"
          className="rr-heat-chip rr-heat-chip--toggle"
          aria-pressed={heat.urgentOnly}
          onClick={() => setHeat({ urgentOnly: !heat.urgentOnly })}
        >
          Samo nujna mesta
        </button>
        <button
          type="button"
          className="rr-heat-chip rr-heat-chip--toggle"
          aria-pressed={heat.hideQuiet}
          onClick={() => setHeat({ hideQuiet: !heat.hideQuiet })}
        >
          Skrij sektorje brez težav
        </button>
      </div>

      <div className="rr-heat-scroller" ref={scrollerRef} onScroll={() => sel && close()}>
        <div
          className="rr-heat-grid"
          ref={gridRef}
          role="grid"
          aria-label="Mesta po sektorjih in urah"
          style={{ '--heat-cols': n } as React.CSSProperties}
          onKeyDown={onGridKey}
        >
          <div className="rr-heat-row rr-heat-axis" role="row">
            <div className="rr-heat-lab" role="columnheader">
              sektor · ura
            </div>
            {model.hours.map((h) => (
              <div
                key={h}
                role="columnheader"
                className={`rr-heat-h${h % 6 === 0 ? ' rr-heat-h--major' : ''}`}
              >
                {h}
              </div>
            ))}
            <div className="rr-heat-sum" role="columnheader">
              {heat.short ? <span className="r">manjka</span> : null}
              {heat.short && heat.pending ? '·' : null}
              {heat.pending ? <span className="a">čaka</span> : null}
            </div>
          </div>

          {model.hours.map((h, i) =>
            h % 6 === 0 && i > 0 ? (
              <div
                key={`g${h}`}
                className="rr-heat-track-x rr-heat-gridline"
                style={{ left: trackX(i, n) }}
              />
            ) : null,
          )}

          {model.days.map((d) => {
            const tone = dayToneAt(d.index);
            const open = isOpen(d);
            const dayRow = navRow++;
            return (
              <div key={d.dayId} className="rr-heat-day" data-day-band={d.dayId} role="rowgroup">
                <div className="rr-heat-row rr-heat-dayrow" role="row">
                  <div className="rr-heat-lab" role="rowheader">
                    <button
                      type="button"
                      className="rr-heat-toggle"
                      aria-expanded={open}
                      onClick={() => toggleDay(d)}
                    >
                      <Chevron />
                      <span className="rr-heat-daytag" style={{ background: tone.tint, color: tone.ink }}>
                        {d.label}
                      </span>
                      <span
                        className="rr-heat-phase"
                        style={{ background: PHASE_TONE[d.phase].bg, color: PHASE_TONE[d.phase].fg }}
                      >
                        {phaseLabel[d.phase]}
                      </span>
                    </button>
                    <span className="rr-heat-daymeta">
                      {shortDate(d.date)}
                      {d.peak ? ` · vrh ${plM(d.peak.open)} ob ${d.peak.hour}h` : ''}
                    </span>
                  </div>
                  {d.hours.map((c, i) => {
                    const key = `b:${d.dayId}:${c.hour}`;
                    const px = (v: number) => (v ? Math.max(3, Math.round((v / Math.max(1, model.maxOpen)) * 48)) : 0);
                    return (
                      <button
                        key={i}
                        type="button"
                        role="gridcell"
                        className={`rr-heat-bar${c.past ? ' rr-heat-past' : ''}`}
                        disabled={!c.open}
                        data-tip="bar"
                        data-day={d.dayId}
                        data-h={c.hour}
                        {...(c.open
                          ? { 'data-heat-key': key, 'data-nav-row': dayRow, 'data-nav-col': i }
                          : {})}
                        data-selected={selKey === key || undefined}
                        tabIndex={c.open ? tabIndexOf(key) : -1}
                        aria-label={`${d.label} ${c.hour}:00: ${c.open} odprtih, manjka ${c.short}, čaka ${c.pending}`}
                        {...pressHandlers(key)}
                        onClick={(ev) =>
                          clickOpen(ev.currentTarget, { kind: 'bar', key, dayId: d.dayId, hour: c.hour })
                        }
                      >
                        <span className="rr-heat-stack">
                          {c.short ? <i style={{ height: px(c.short), background: 'var(--heat-bar-short)' }} /> : null}
                          {c.pending ? (
                            <i style={{ height: px(c.pending), background: 'var(--heat-bar-pending)' }} />
                          ) : null}
                          {c.neutral ? (
                            <i style={{ height: px(c.neutral), background: 'var(--heat-bar-neutral)' }} />
                          ) : null}
                          {c.ok ? <i style={{ height: px(c.ok), background: 'var(--heat-bar-ok)' }} /> : null}
                        </span>
                      </button>
                    );
                  })}
                  <div className="rr-heat-sum">
                    <Summary short={heat.short ? d.summary.short : null} pending={heat.pending ? d.summary.pending : null} />
                    <span className="rr-heat-sumcap">mest v dnevu</span>
                  </div>
                </div>

                {open
                  ? d.sectors.map((s) => {
                      const secRow = navRow++;
                      const secOpen = !!heat.sectors[s.key];
                      return (
                        <div key={s.key} role="presentation">
                          <div className="rr-heat-row rr-heat-sec" role="row">
                            <div className="rr-heat-lab" role="rowheader">
                              <button
                                type="button"
                                className="rr-heat-toggle"
                                aria-expanded={secOpen}
                                onClick={() => toggleSector(s.key)}
                              >
                                <Chevron />
                                <span className="rr-heat-secname">{s.name}</span>
                                <span className="rr-heat-seccount">{s.posts.length}</span>
                              </button>
                            </div>
                            {s.cells.map((c, i) => {
                              const key = `c:${s.key}:${c.hour}`;
                              const past = c.past ? ' rr-heat-past' : '';
                              const tip = { 'data-tip': 'cell', 'data-day': d.dayId, 'data-sector': s.key, 'data-h': c.hour };
                              if (c.kind === 'empty' || c.kind === 'ok' || c.kind === 'neutral') {
                                return (
                                  <div key={i} className="rr-heat-cellwrap" role="gridcell">
                                    <div className={`rr-heat-cell rr-heat-cell--${c.kind}${past}`} {...tip} />
                                  </div>
                                );
                              }
                              const value = c.kind === 'short' ? c.short : c.pending;
                              return (
                                <div key={i} className="rr-heat-cellwrap" role="gridcell">
                                  <button
                                    type="button"
                                    className={`rr-heat-cell rr-heat-cell--${c.kind} b${c.bin}${past}`}
                                    {...tip}
                                    data-heat-key={key}
                                    data-nav-row={secRow}
                                    data-nav-col={i}
                                    data-selected={selKey === key || undefined}
                                    tabIndex={tabIndexOf(key)}
                                    aria-label={`${d.label}, ${s.name}, ${c.hour}:00: manjka ${c.short}, čaka ${c.pending}, brez težav ${c.ok}`}
                                    {...pressHandlers(key)}
                                    onClick={(ev) =>
                                      clickOpen(ev.currentTarget, {
                                        kind: 'cell',
                                        key,
                                        dayId: d.dayId,
                                        sectorKey: s.key,
                                        hour: c.hour,
                                      })
                                    }
                                  >
                                    {value}
                                    {c.corner ? <span className="rr-heat-corner" aria-hidden="true" /> : null}
                                  </button>
                                </div>
                              );
                            })}
                            <div className="rr-heat-sum">
                              <Summary
                                short={heat.short ? s.summary.short : null}
                                pending={heat.pending ? s.summary.pending : null}
                              />
                            </div>
                          </div>

                          {secOpen
                            ? s.posts.map((p) => {
                                const postRow = navRow++;
                                return (
                                  <div key={p.postId} className="rr-heat-row rr-heat-post" role="row">
                                    <div className="rr-heat-lab" role="rowheader">
                                      <span className="rr-heat-code">{p.code}</span>
                                      {p.urgent ? <span className="rr-heat-urgent">nujno</span> : null}
                                      <span className="rr-heat-micro">{p.micro}</span>
                                    </div>
                                    <div className="rr-heat-strip" role="gridcell">
                                      {p.coverage.map((sp) => {
                                        const from = model.hours[0]! * 60;
                                        const span = n * 60;
                                        const style = {
                                          left: `${((sp.fromMin - from) / span) * 100}%`,
                                          width: `calc(${((sp.toMin - sp.fromMin) / span) * 100}% - 1px)`,
                                        };
                                        const past =
                                          d.phase === 'past' || (d.phase === 'live' && sp.toMin <= clock.minutes)
                                            ? ' rr-heat-past'
                                            : '';
                                        const common = {
                                          'data-tip': 'span',
                                          'data-day': d.dayId,
                                          'data-sector': s.key,
                                          'data-post': p.postId,
                                          'data-from': sp.fromMin,
                                        };
                                        if (!spanShown(sp.state, lenses)) {
                                          const cls =
                                            sp.state === 'ok' || sp.state === 'external' ? sp.state : 'neutral';
                                          return (
                                            <span
                                              key={sp.fromMin}
                                              className={`rr-heat-span rr-heat-span--${cls}${past}`}
                                              style={style}
                                              {...common}
                                            />
                                          );
                                        }
                                        const key = `s:${d.dayId}:${p.postId}:${sp.fromMin}`;
                                        const col = Math.max(0, Math.floor(sp.fromMin / 60) - model.hours[0]!);
                                        return (
                                          <button
                                            key={sp.fromMin}
                                            type="button"
                                            className={`rr-heat-span rr-heat-span--${sp.state}${past}`}
                                            style={style}
                                            {...common}
                                            data-heat-key={key}
                                            data-nav-row={postRow}
                                            data-nav-col={col}
                                            data-selected={selKey === key || undefined}
                                            tabIndex={tabIndexOf(key)}
                                            aria-label={`${p.code} ${m2t(sp.fromMin)}–${m2t(sp.toMin)}`}
                                            {...pressHandlers(key)}
                                            onClick={(ev) =>
                                              clickOpen(ev.currentTarget, {
                                                kind: 'span',
                                                key,
                                                dayId: d.dayId,
                                                sectorKey: s.key,
                                                postId: p.postId,
                                                fromMin: sp.fromMin,
                                              })
                                            }
                                          />
                                        );
                                      })}
                                    </div>
                                    <div className="rr-heat-sum" />
                                  </div>
                                );
                              })
                            : null}
                        </div>
                      );
                    })
                  : null}

                {open && d.empty ? (
                  <div className="rr-heat-empty">
                    {d.empty === 'no-windows'
                      ? 'Ta dan ni odprtih mest.'
                      : d.empty === 'no-urgent'
                        ? 'Ta dan nima nujnih mest.'
                        : 'Vsi sektorji so ta dan brez težav.'}
                  </div>
                ) : null}

                {d.phase === 'live' &&
                clock.minutes >= model.hours[0]! * 60 &&
                clock.minutes < (model.hours.at(-1)! + 1) * 60 ? (
                  <div
                    className="rr-heat-track-x rr-heat-nowline"
                    style={{ left: trackX((clock.minutes - model.hours[0]! * 60) / 60, n) }}
                  >
                    <span>zdaj {m2t(clock.minutes)}</span>
                  </div>
                ) : null}
              </div>
            );
          })}

          <HeatOverlay gridRef={gridRef} model={model} flash={flash} />

          <Popover
            opened={!!sel}
            onDismiss={close}
            position="bottom"
            width={400}
            shadow="md"
            radius={12}
            trapFocus
            returnFocus
            withinPortal
          >
            <Popover.Target>
              <span
                className="rr-heat-anchor"
                style={sel ? sel.box : { left: 0, top: 0, width: 0, height: 0 }}
              />
            </Popover.Target>
            <Popover.Dropdown p={0}>
              {sel ? (
                <PopoverBody
                  sel={sel.sel}
                  model={model}
                  lenses={lenses}
                  canComms={canComms}
                  onAction={(fn) => {
                    close();
                    fn();
                  }}
                  actions={{ goGap, goShift, goPerson, goTimelineAt }}
                  onOpenSector={(dayId, key) => {
                    close();
                    setHeat({
                      days: { ...(heat.days ?? defaultDays(data, clock)), [dayId]: true },
                      sectors: { ...heat.sectors, [key]: true },
                    });
                  }}
                />
              ) : null}
            </Popover.Dropdown>
          </Popover>
        </div>
      </div>
    </section>
  );
});

function spanShown(state: string, lenses: HeatLenses): boolean {
  if (state === 'gap' || state === 'rejected') return lenses.short;
  if (state === 'pending') return lenses.pending;
  return false;
}

function Summary({ short, pending }: { short: number | null; pending: number | null }) {
  return (
    <span style={{ display: 'flex', gap: 8 }}>
      {short !== null ? short ? <span className="r">{short}</span> : <span className="z">–</span> : null}
      {pending !== null ? pending ? <span className="a">{pending}</span> : <span className="z">–</span> : null}
    </span>
  );
}

type Actions = Pick<ReturnType<typeof useDeepLinks>, 'goGap' | 'goShift' | 'goPerson' | 'goTimelineAt'>;

function PopoverBody({
  sel,
  model,
  lenses,
  canComms,
  actions,
  onAction,
  onOpenSector,
}: {
  sel: Sel;
  model: HeatModel;
  lenses: HeatLenses;
  canComms: boolean;
  actions: Actions;
  onAction: (fn: () => void) => void;
  onOpenSector: (dayId: string, sectorKey: string) => void;
}) {
  if (sel.kind === 'bar') {
    const at = locateHour(model, sel.dayId, sel.hour);
    if (!at) return null;
    const { day, index, cell: c } = at;
    const { sectors: rows, hidden } = barSectors(day, index);
    return (
      <>
        <div className="rr-heat-pop-head">
          <span className="rr-heat-pop-title">
            {day.label} · {c.hour}:00–{c.hour + 1}:00
          </span>
          <span className="rr-heat-pop-sub">po sektorjih</span>
        </div>
        <div className="rr-heat-pop-items">
          {rows.map((s) => {
            const sc = s.cells[index]!;
            return (
              <button
                key={s.key}
                type="button"
                className="rr-heat-pop-item"
                onClick={() => onOpenSector(day.dayId, s.key)}
              >
                <span className="rr-heat-pop-l1">
                  <b style={{ fontWeight: 600 }}>{s.name}</b>
                </span>
                <span className="rr-heat-pop-l2">
                  <span style={{ color: 'oklch(0.47 0.13 25)', fontWeight: 700 }}>manjka {sc.short}</span> ·{' '}
                  <span style={{ color: 'oklch(0.48 0.1 75)', fontWeight: 600 }}>čaka {sc.pending}</span> · brez
                  težav {sc.ok}
                </span>
                <span className="rr-heat-pop-act">Odpri mesta →</span>
              </button>
            );
          })}
        </div>
        {hidden ? (
          <div className="rr-heat-pop-foot">
            {hidden === 1 ? '1 sektor brez težav je skrit' : `${hidden} sektorjev brez težav je skritih`}
          </div>
        ) : null}
      </>
    );
  }

  if (sel.kind === 'cell') {
    const at = locateHour(model, sel.dayId, sel.hour, sel.sectorKey);
    if (!at?.sector) return null;
    const { day, sector, index, cell: c } = at;
    const { short, pending, settled } = cellRows(sector, index, c.hour, lenses);
    return (
      <>
        <div className="rr-heat-pop-head">
          <span className="rr-heat-pop-title">
            {sector.name} · {day.label} {c.hour}:00–{c.hour + 1}:00
          </span>
          <span className="rr-heat-pop-sub">{plM(sector.posts.length)} v sektorju ta dan</span>
        </div>
        <div className="rr-heat-pop-items">
          <Group label="Manjka oseba" swatch="var(--heat-r3)" rows={short} {...{ day, canComms, actions, onAction }} />
          <Group label="Čaka potrditev" swatch="var(--heat-a4)" rows={pending} {...{ day, canComms, actions, onAction }} />
        </div>
        <div className="rr-heat-pop-foot">
          {settled ? `še ${plM(settled)} brez težav` : null}
          <button
            type="button"
            onClick={() => onAction(() => actions.goTimelineAt(day.dayId, sector.name, c.hour * 60 + 30))}
          >
            Odpri v časovnici →
          </button>
        </div>
      </>
    );
  }

  const found = locateSpan(model, sel.dayId, sel.sectorKey, sel.postId, sel.fromMin);
  if (!found) return null;
  const { day, sector, post, span } = found;
  const rows = rowsAt(post, span.fromMin, span.toMin, lenses);
  return (
    <>
      <div className="rr-heat-pop-head">
        <span className="rr-heat-pop-title">
          {post.code} · {day.label}
        </span>
        <span className="rr-heat-pop-sub">{post.micro}</span>
      </div>
      <div className="rr-heat-pop-items">
        {rows.map((r) => (
          <RowItem key={r.key} row={r} {...{ day, canComms, actions, onAction }} />
        ))}
      </div>
      <div className="rr-heat-pop-foot">
        <button
          type="button"
          onClick={() => onAction(() => actions.goTimelineAt(day.dayId, sector.name, span.fromMin))}
        >
          Odpri v časovnici →
        </button>
      </div>
    </>
  );
}

function Group({
  label,
  swatch,
  rows,
  ...rest
}: {
  label: string;
  swatch: string;
  rows: HeatRow[];
  day: HeatDay;
  canComms: boolean;
  actions: Actions;
  onAction: (fn: () => void) => void;
}) {
  if (!rows.length) return null;
  return (
    <>
      <div className="rr-heat-pop-group">
        <i style={{ background: swatch }} />
        {label} · {rows.length}
      </div>
      {rows.map((r) => (
        <RowItem key={r.key} row={r} {...rest} />
      ))}
    </>
  );
}

function RowItem({
  row,
  day,
  canComms,
  actions,
  onAction,
}: {
  row: HeatRow;
  day: HeatDay;
  canComms: boolean;
  actions: Actions;
  onAction: (fn: () => void) => void;
}) {
  const range = `${m2t(row.fromMin)}–${m2t(row.toMin)}`;
  const text =
    row.state === 'gap'
      ? `vrzel ${range}`
      : row.state === 'rejected'
        ? `zavrnjeno · ${row.name ?? '?'} · ${range}`
        : `${row.name ?? '?'} · ${range}`;

  let act: { label: string; run: () => void } | null = null;
  if (row.state === 'gap') {
    act = {
      label: 'Poišči kandidata →',
      run: () =>
        actions.goGap({ eventDayId: day.dayId, postId: row.postId, fromMin: row.fromMin, toMin: row.toMin }),
    };
  } else if (row.state === 'rejected' && row.shiftId) {
    const shiftId = row.shiftId;
    act = { label: 'Odpri v urniku →', run: () => actions.goShift(day.dayId, shiftId) };
  } else if (row.state === 'pending' && row.volunteerId) {
    const id = row.volunteerId;
    act = canComms
      ? { label: 'Pošlji opomnik →', run: () => actions.goPerson(id, 'sporocila') }
      : { label: 'Odpri profil →', run: () => actions.goPerson(id) };
  }

  const body = (
    <>
      <span className="rr-heat-pop-l1">
        <span className="rr-heat-code">{row.code}</span>
        {row.urgent ? <span className="rr-heat-urgent">nujno</span> : null}
        <span className="rr-heat-micro">{row.micro}</span>
      </span>
      <span className="rr-heat-pop-l2">{text}</span>
      {act ? <span className="rr-heat-pop-act">{act.label}</span> : null}
    </>
  );
  return act ? (
    <button type="button" className="rr-heat-pop-item" onClick={() => onAction(act.run)}>
      {body}
    </button>
  ) : (
    <div className="rr-heat-pop-item">{body}</div>
  );
}
